import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useManufacturingOrders, useCreateManufacturingOrder } from '@/hooks/useMES';
import {
  MOStatus,
  Priority,
  MO_STATUS_LABELS,
  MO_STATUS_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  IMOFilter,
  ICreateMORequest,
} from '@/types/mes';
import { MobileCard } from '@/components/ui';

const MOListPage = () => {
  const { t } = useTranslation('mes');
  const navigate = useNavigate();
  const [filter, setFilter] = useState<IMOFilter>({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data: mos = [], isLoading, error } = useManufacturingOrders(filter);
  const createMO = useCreateManufacturingOrder();

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const handleCreateMO = async (data: ICreateMORequest) => {
    try {
      const newMO = await createMO.mutateAsync(data);
      setShowCreateDialog(false);
      navigate(`/app/mes/orders/${newMO.id}`);
    } catch (err) {
      logger.error('Failed to create MO:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
        {t('error.loading', 'Failed to load manufacturing orders')}
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('mo.title', 'Manufacturing Orders')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {t('mo.subtitle', 'Manage production orders and work flows')}
          </p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="w-full xs:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 min-h-[44px]"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="sm:inline">{t('mo.create', 'Create MO')}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col xs:flex-row gap-3 p-3 sm:p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('filter.status', 'Status')}
          </label>
          <select
            value={filter.status || ''}
            onChange={(e) => setFilter({ ...filter, status: (e.target.value || undefined) as MOStatus })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px]"
          >
            <option value="">{t('filter.all', 'All')}</option>
            {Object.entries(MO_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('filter.priority', 'Priority')}
          </label>
          <select
            value={filter.priority || ''}
            onChange={(e) => setFilter({ ...filter, priority: (e.target.value || undefined) as Priority })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px]"
          >
            <option value="">{t('filter.all', 'All')}</option>
            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* MO List */}
      {mos.length === 0 ? (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {t('mo.empty', 'No manufacturing orders found')}
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {mos.map((mo) => (
              <MobileCard
                key={mo.id}
                title={mo.mo_number}
                subtitle={`${mo.product_name} (${mo.product_code})`}
                status={
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${MO_STATUS_COLORS[mo.status]}`}>
                    {MO_STATUS_LABELS[mo.status]}
                  </span>
                }
                fields={[
                  {
                    label: t('mo.priority', 'Priority'),
                    value: (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${PRIORITY_COLORS[mo.priority]}`}>
                        {PRIORITY_LABELS[mo.priority]}
                      </span>
                    ),
                  },
                  {
                    label: t('mo.quantity', 'Qty'),
                    value: `${mo.completed_qty} / ${mo.planned_qty}`,
                    highlight: true,
                  },
                  {
                    label: t('mo.schedule', 'Schedule'),
                    value: `${formatDate(mo.scheduled_start)} - ${formatDate(mo.scheduled_end)}`,
                  },
                ]}
                onClick={() => navigate(`/app/mes/orders/${mo.id}`)}
                showChevron
              />
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('mo.number', 'MO Number')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('mo.product', 'Product')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('mo.status', 'Status')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('mo.priority', 'Priority')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('mo.quantity', 'Quantity')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('mo.schedule', 'Schedule')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('mo.actions', 'Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {mos.map((mo) => (
                    <tr
                      key={mo.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => navigate(`/app/mes/orders/${mo.id}`)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {mo.mo_number}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {mo.product_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {mo.product_code} (Rev {mo.product_revision})
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${MO_STATUS_COLORS[mo.status]}`}>
                          {MO_STATUS_LABELS[mo.status]}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[mo.priority]}`}>
                          {PRIORITY_LABELS[mo.priority]}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <span className="text-green-600 dark:text-green-400">{mo.completed_qty}</span>
                          <span className="text-gray-400"> / </span>
                          <span className="text-gray-900 dark:text-white">{mo.planned_qty}</span>
                        </div>
                        {mo.scrapped_qty > 0 && (
                          <p className="text-xs text-red-500">
                            {mo.scrapped_qty} scrapped
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(mo.scheduled_start)} - {formatDate(mo.scheduled_end)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/app/mes/orders/${mo.id}`);
                          }}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {t('mo.view', 'View')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create MO Dialog - Simple placeholder */}
      {showCreateDialog && (
        <CreateMODialog
          onClose={() => setShowCreateDialog(false)}
          onCreate={handleCreateMO}
          isLoading={createMO.isLoading}
        />
      )}
    </div>
  );
};

// Simple Create MO Dialog component
interface CreateMODialogProps {
  onClose: () => void;
  onCreate: (data: ICreateMORequest) => void;
  isLoading: boolean;
}

const CreateMODialog = ({ onClose, onCreate, isLoading }: CreateMODialogProps) => {
  const { t } = useTranslation('mes');
  const [formData, setFormData] = useState<ICreateMORequest>({
    product_id: '',
    product_revision: '',
    planned_qty: 0,
    priority: 'normal',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-xl p-4 sm:p-6 w-full sm:max-w-md sm:mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('mo.createTitle', 'Create Manufacturing Order')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('mo.productId', 'Product ID')}
            </label>
            <input
              type="text"
              required
              value={formData.product_id}
              onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('mo.revision', 'Revision')}
            </label>
            <input
              type="text"
              required
              value={formData.product_revision}
              onChange={(e) => setFormData({ ...formData, product_revision: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('mo.plannedQty', 'Planned Quantity')}
            </label>
            <input
              type="number"
              required
              min={1}
              value={formData.planned_qty}
              onChange={(e) => setFormData({ ...formData, planned_qty: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('mo.priority', 'Priority')}
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px]"
            >
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col-reverse xs:flex-row justify-end gap-2 xs:gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="w-full xs:w-auto px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors min-h-[44px]"
            >
              {t('actions.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full xs:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 min-h-[44px] font-medium"
            >
              {isLoading ? t('actions.creating', 'Creating...') : t('actions.create', 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MOListPage;
